import { Link } from 'react-router-dom';
import { Phone, Award, Users, Handshake, Target, ChevronRight } from 'lucide-react';

// Equipo de construcción profesional
const CONSTRUCTION_TEAM = "https://images.unsplash.com/photo-1760009436767-d154e930e55c?w=800";
// Granada - Alhambra con Sierra Nevada
const GRANADA_IMAGE = "https://images.unsplash.com/photo-1564740603199-5f56138c6679?w=800";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_661024ed-50d4-4d78-a636-dd6004c5bf2a/artifacts/ri07y6ac_WhatsApp%20Image%202026-02-10%20at%2015.11.53.png";

const SobreNosotrosPage = () => {
  const valores = [
    {
      icon: <Award className="w-8 h-8" />,
      title: "Experiencia",
      description: "Más de 30 años en el sector de la construcción nos avalan. Conocemos el mercado y las necesidades de los profesionales."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Trato Cercano",
      description: "Somos una empresa familiar. Atendemos personalmente a cada cliente, ya sea profesional o particular."
    },
    {
      icon: <Handshake className="w-8 h-8" />,
      title: "Compromiso",
      description: "Cumplimos lo que prometemos. Plazos de entrega, disponibilidad de stock y asesoramiento profesional."
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Especialización",
      description: "Nos centramos en lo que mejor sabemos hacer: yesos y sistemas de yeso laminado (pladur) para profesionales."
    }
  ];

  return (
    <div className="industrial-bg" data-testid="sobre-nosotros-page">
      {/* Hero */}
      <section className="bg-[#1A365D] py-16">
        <div className="section-container">
          <nav className="text-sm text-gray-400 mb-4" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2">
              <li><Link to="/" className="hover:text-white">Inicio</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li className="text-white">Sobre Nosotros</li>
            </ol>
          </nav>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase mb-4">
            Empresa de Yesos y Yeso Laminado (Pladur) en Motril
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            Conoce a Fornes e Hijos: profesionales del yeso laminado en Motril con más de 30 años 
            de experiencia en el sector de la construcción.
          </p>
        </div>
      </section>

      {/* Historia */}
      <section className="py-20">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={LOGO_URL}
                alt="Logo Fornes e Hijos - Empresa de yesos y yeso laminado en Motril"
                className="w-64 h-auto mb-8"
              />
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A365D] uppercase mb-6">
                Nuestra Historia
              </h2>
              <div className="prose prose-lg text-[#4A5568]">
                <p>
                  <strong>Fornes e Hijos</strong> nace de la pasión por el sector de la construcción 
                  y el compromiso con la calidad. Aunque nuestra tienda es de reciente apertura, 
                  contamos con <strong>más de 30 años de experiencia en el sector</strong> de yesos, 
                  escayolas y sistemas de yeso laminado (pladur).
                </p>
                <p>
                  Durante décadas hemos trabajado codo a codo con yesistas, escayolistas e instaladores 
                  de yeso laminado en la provincia de Granada. Esta experiencia nos ha permitido conocer de 
                  primera mano las necesidades de los profesionales: materiales de calidad, disponibilidad 
                  inmediata, servicio ágil y asesoramiento técnico competente.
                </p>
                <p>
                  Ubicados en el <strong>Polígono Alborán de Motril</strong>, nuestro almacén está 
                  estratégicamente situado para dar servicio a toda la Costa Tropical y el Valle de 
                  Lecrín. Desde aquí suministramos a profesionales y también atendemos a particulares 
                  que buscan materiales de calidad para sus reformas.
                </p>
              </div>
            </div>
            <div>
              <img
                src={GRANADA_IMAGE}
                alt="Granada y Sierra Nevada - Nuestra zona de servicio"
                className="w-full h-auto rounded-sm shadow-lg"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-20 bg-white">
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A365D] uppercase mb-4">
              Nuestros Valores
            </h2>
            <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
              Lo que nos define y nos diferencia como empresa de materiales de construcción en Motril.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {valores.map((valor, index) => (
              <div
                key={index}
                className="bg-[#F7FAFC] p-8 rounded-sm border border-gray-200 card-hover"
                data-testid={`valor-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#3182CE]/10 text-[#3182CE] rounded-sm mb-6">
                  {valor.icon}
                </div>
                <h3 className="font-heading text-xl font-bold text-[#1A365D] mb-3">
                  {valor.title}
                </h3>
                <p className="text-[#4A5568]">{valor.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* A quién servimos */}
      <section className="py-20 industrial-bg">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src={CONSTRUCTION_TEAM}
                alt="Equipo profesional de construcción en Motril"
                className="w-full h-auto rounded-sm shadow-lg"
                loading="lazy"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A365D] uppercase mb-6">
                A Quién Servimos
              </h2>
              <div className="prose prose-lg text-[#4A5568] mb-8">
                <p>
                  Nuestro público principal son los <strong>profesionales de la construcción</strong>: 
                  yesistas, escayolistas, instaladores de yeso laminado (pladur) y empresas de reforma. Entendemos 
                  sus necesidades porque hemos trabajado junto a ellos durante más de tres décadas.
                </p>
                <p>
                  Sabemos que los profesionales valoran la <strong>rapidez</strong>, la 
                  <strong> disponibilidad de stock</strong> y la <strong>fiabilidad</strong> en los 
                  plazos de entrega. Por eso mantenemos un amplio inventario de los materiales más 
                  demandados y ofrecemos servicio a obra con entregas en 24-48 horas.
                </p>
                <p>
                  También <strong>atendemos a particulares</strong> que buscan materiales de calidad 
                  para sus reformas. Les ofrecemos el mismo asesoramiento técnico y la misma calidad 
                  de servicio que a los profesionales.
                </p>
              </div>
              <div className="bg-white p-6 rounded-sm border border-gray-200">
                <h3 className="font-heading text-lg font-bold text-[#1A365D] mb-3">
                  Nuestros Clientes:
                </h3>
                <ul className="grid grid-cols-2 gap-2 text-[#4A5568]">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#3182CE] rounded-full"></div>
                    Yesistas
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#3182CE] rounded-full"></div>
                    Escayolistas
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#3182CE] rounded-full"></div>
                    Instaladores yeso laminado
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#3182CE] rounded-full"></div>
                    Empresas de reforma
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#3182CE] rounded-full"></div>
                    Constructoras
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#3182CE] rounded-full"></div>
                    Particulares
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-[#1A365D]">
        <div className="section-container text-center">
          <h2 className="font-heading text-3xl font-bold text-white uppercase mb-4">
            ¿Trabajas en el Sector?
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Visítanos en nuestro almacén o llámanos. Estaremos encantados de conocerte y 
            convertirte en tu proveedor de confianza.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:858130213"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#1A365D] font-semibold px-6 py-3 rounded-sm hover:bg-gray-100 transition-colors"
              data-testid="nosotros-cta-call"
            >
              <Phone className="w-5 h-5" />
              858 130 213
            </a>
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-semibold px-6 py-3 rounded-sm hover:bg-white/10 transition-colors"
              data-testid="nosotros-cta-contacto"
            >
              Contactar
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SobreNosotrosPage;
